import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

import type { Finding, FindingKind, Locale } from "../../../shared/types";
import type { DocumentSummary } from "../lib/api";
import { useCopy } from "../lib/copy";
import DataChat from "./DataChat";

interface NotesProps {
  locale: Locale;
  findings: Finding[];
  /** What has been handed to the consultant to read, for this case. */
  documents: DocumentSummary[];
  /** The rail is the CASE's dossier, so the export it offers has to be the case's too. */
  caseId: number | null;
  /** Opens the file picker the stage owns. Quiet — this rail is not a dropzone. */
  onAttach: () => void;
  onDeleteDocument: (id: number) => void;
}

// Problems and the suspicions under them are what a founder came for; the open
// questions are what happens next. Facts sit between them as the evidence.
const ORDER: FindingKind[] = ["problem", "hypothesis", "fact", "recommendation", "question"];

/** The first snap point. It shows the notes without covering the blob. */
const SHEET_OPEN = "45dvh";

/**
 * How long a note keeps its arrival class. Long enough for the entrance and its
 * stagger to finish, then gone, so a later re-render cannot replay it.
 */
const LAND_MS = 900;

export default function Notes({
  locale,
  findings,
  documents,
  caseId,
  onAttach,
  onDeleteDocument,
}: NotesProps) {
  const copy = useCopy(locale);
  const [sheetMode, setSheetMode] = useState(false);
  const [open, setOpen] = useState(false);
  /** Removing a document is two presses in the same row. No dialogs here either. */
  const [confirmingDoc, setConfirmingDoc] = useState<number | null>(null);
  /** The data chat, opened from the foot of the rail and living over the top of it. */
  const [asking, setAsking] = useState(false);
  const askRef = useRef<HTMLButtonElement | null>(null);
  const railRef = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  /**
   * A finding landing.
   *
   * This is the moment the product is for: you are mid-sentence about your own
   * business and a note you did not write appears in the margin. It was animating
   * every note in the rail on every first paint instead — a wave of thirty rows on
   * open, which spends the entrance where nothing happened and leaves nothing to
   * spend when something does. So only ids that were not here a moment ago move, in
   * the order they arrived, and the first render of the rail records what is there
   * and animates none of it (never-animate rule 5, and the same shape the
   * participant rows on the study board use).
   */
  const [landed, setLanded] = useState<number[]>([]);
  const seenRef = useRef<Set<number> | null>(null);

  useEffect(() => {
    const ids = findings.map((finding) => finding.id);
    const before = seenRef.current;
    seenRef.current = new Set(ids);
    if (before === null) return;
    const added = ids.filter((id) => !before.has(id));
    if (added.length === 0) return;
    setLanded(added);
    const timer = window.setTimeout(() => setLanded([]), LAND_MS);
    return () => window.clearTimeout(timer);
  }, [findings]);

  // Below 1200px the rail is a sheet, and a sheet that is closed must be out of the
  // tab order as well as off the screen.
  useEffect(() => {
    const query = window.matchMedia("(max-width: 1199px)");
    const sync = (): void => setSheetMode(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!open) return;
    railRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const groups = ORDER.map((kind) => ({
    kind,
    items: findings.filter((finding) => finding.kind === kind),
  })).filter((group) => group.items.length > 0);

  // Three bands, not a number: a percentage would be a precision the extraction cannot
  // back up. Above 0.75 an unmarked finding is an asserted one.
  const band = (confidence: number): string | null =>
    confidence < 0.4
      ? copy.confidenceMaybe
      : confidence <= 0.75
        ? copy.confidenceLikely
        : null;

  const closed = sheetMode && !open;

  return (
    <>
      <button
        ref={triggerRef}
        className="notes-trigger btn btn--quiet btn--sm"
        type="button"
        aria-expanded={open}
        aria-controls="notes"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="num" style={{ textTransform: "uppercase" }}>
          {copy.notesCount(findings.length)}
        </span>
      </button>

      <aside
        id="notes"
        className="notes"
        ref={railRef}
        aria-labelledby="notes-header"
        tabIndex={sheetMode ? -1 : undefined}
        inert={closed}
        style={{ ["--sheet-h"]: open ? SHEET_OPEN : "0dvh" } as CSSProperties}
      >
        {sheetMode && <div className="notes__grab" aria-hidden="true" />}

        {/* The count is a sibling, not a child: inside the heading its accessible name
            came out as "What I have so far eight notes", one phrase with no seam. */}
        <div className="notes__title">
          <h2 id="notes-header" className="notes__header">
            {copy.notesHeader}
          </h2>
          <span className="notes__count num">{copy.notesCount(findings.length)}</span>
        </div>
        <span className="notes__caveat label">{copy.notesCaveat}</span>

        {findings.length === 0 ? (
          <p className="notes__empty" style={{ marginTop: "var(--s8)" }}>
            {copy.notesEmpty}
          </p>
        ) : (
          groups.map((group) => (
            <section key={group.kind}>
              <h3 className="label" style={{ margin: "var(--s9) 0 0" }}>
                {copy.findingKind[group.kind]}
              </h3>
              <ul className="notes__list" style={{ marginTop: "var(--s5)" }}>
                {group.items.map((finding) => {
                  const suffix = band(finding.confidence);
                  // Staggered in the order they ARRIVED, which for a note landing
                  // mid-conversation is one row and one delay of zero.
                  const landing = landed.indexOf(finding.id);
                  // The conversation this came out of has been deleted, which took
                  // its transcript with it: the note stays because it is still
                  // something that was said, and it says here that there is nothing
                  // left to check it against. (The field is on the wire from
                  // services/dossier.ts; its declaration belongs on `Finding` in
                  // shared/types.ts, which another hand owns tonight.)
                  const sourceless =
                    (finding as { sourceless?: boolean }).sourceless === true;
                  return (
                    <li
                      className={landing === -1 ? "note-item" : "note-item note-item--landing"}
                      key={finding.id}
                      style={{ ["--i"]: Math.max(landing, 0) } as CSSProperties}
                    >
                      {(suffix !== null || sourceless) && (
                        <span className="note-item__meta label">
                          {suffix}
                          {suffix !== null && sourceless && (
                            <span aria-hidden="true"> · </span>
                          )}
                          {sourceless && copy.notesSourceless}
                        </span>
                      )}
                      <p className="note-item__title">{finding.title}</p>
                      {finding.detail !== null && finding.detail.length > 0 && (
                        <p className="note-item__detail">{finding.detail}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}

        {/* What the consultant has been given to read. A quiet list, never a
            dropzone: attaching is one small action at the foot of it. */}
        {(documents.length > 0 || caseId !== null) && (
          <section className="docs">
            {documents.length > 0 && (
              <>
                <h3 className="label" style={{ margin: "var(--s9) 0 0" }}>
                  {copy.documentsLabel}
                </h3>
                <ul className="notes__list" style={{ marginTop: "var(--s5)" }}>
                  {documents.map((doc) => (
                    <li className="doc-item" key={doc.id}>
                      {confirmingDoc === doc.id ? (
                        <div className="doc-item__confirm">
                          <span className="doc-item__confirm-text">
                            {copy.documentDeleteConfirm}
                          </span>
                          <button
                            className="case__danger label"
                            type="button"
                            onClick={() => {
                              setConfirmingDoc(null);
                              onDeleteDocument(doc.id);
                            }}
                          >
                            {copy.deleteYes}
                          </button>
                          <button
                            className="doc-item__keep label"
                            type="button"
                            onClick={() => setConfirmingDoc(null)}
                          >
                            {copy.deleteNo}
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="doc-item__head">
                            <p className="doc-item__name">{doc.filename}</p>
                            <button
                              className="doc-item__delete label"
                              type="button"
                              onClick={() => setConfirmingDoc(doc.id)}
                            >
                              {copy.deleteCta}
                            </button>
                          </div>
                          {doc.summary !== null && doc.summary.length > 0 && (
                            <p className="doc-item__summary">{doc.summary}</p>
                          )}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {caseId !== null && (
              <button className="docs__attach label" type="button" onClick={onAttach}>
                {copy.documentAttachCta}
              </button>
            )}
          </section>
        )}

        {caseId !== null && findings.length > 0 && (
          <div className="export">
            {/* A plain download: there is nothing to wait for, so nothing to indicate.

                The whole case, not one conversation. This rail shows the case dossier
                and the button says "everything we covered", so exporting a single
                session under it handed a founder a third of what was promised. No
                `download` attribute either: it beat the server's own dated filename
                and the file arrived named after a database row. */}
            <a className="btn btn--primary" href={`/api/export.md?caseId=${caseId}`}>
              {copy.exportCta}
            </a>
            {/* The same live findings as CSV, for whoever works in a spreadsheet. */}
            <a className="btn btn--quiet export__csv" href={`/api/export.csv?caseId=${caseId}`}>
              {copy.exportCsvCta}
            </a>
            <span className="export__sub label">{copy.exportSub}</span>
          </div>
        )}

        {/* The way into the data chat, and deliberately the quietest thing in the
            rail: one line under the export, the same weight as "attach a file".
            The record is what the rail is already showing; this is the sentence
            that says you can talk to it. Nothing about it belongs on the stage. */}
        {caseId !== null && (
          <button
            ref={askRef}
            className="ask-open label"
            type="button"
            aria-expanded={asking}
            onClick={() => setAsking(true)}
          >
            {copy.askCta}
          </button>
        )}
      </aside>

      {/* Mounted OUTSIDE the rail, in `position: fixed`, and that is load-bearing:
          the stage's grid must not move because a panel opened, and a child of the
          rail would inherit its scroll and its sticky positioning. */}
      {asking && caseId !== null && (
        <DataChat
          locale={locale}
          scope={{ kind: "case", id: caseId }}
          onClose={() => {
            setAsking(false);
            // Back to the line that opened it. Closing a panel with Escape and
            // landing at the top of the document is how a keyboard loses its place.
            askRef.current?.focus();
          }}
        />
      )}
    </>
  );
}
